import React, { useState } from "react";
import { filter } from "lodash";
import { Icon } from "@iconify/react";
import plusFill from "@iconify/icons-eva/plus-fill";
import arrowFill from "@iconify/icons-eva/arrow-back-fill";

// material
import {
  Box,
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Tooltip,
  IconButton,
  InputAdornment,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import * as locales from "@material-ui/core/locale";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// components
import Page from "../components/Page";
import Label from "../components/Label";
import Scrollbar from "../components/Scrollbar";
import { TablaHead } from "../components/_dashboard/tablas";
import AgregarPlan from "../components/tipoplanes/AgregarPlan";
import EditarPlan from "src/components/tipoplanes/EditarPlan";
import { tiposApi } from "src/services/tiposApi";
import { AlertaModalV2 } from "src/components/AlertaSwall";
import { useSnackbar } from "src/components/snackbar";
import { useHistorialVisita } from "src/hooks/useHistorialVisita";

// ==================== CONSTANTES ====================

const TABLE_HEAD = [
  { id: "nombre", label: "Nombre Plan", alignRight: false },
  { id: "valorPlan", label: "Valor / Moneda", alignRight: false },
  { id: "categoria", label: "Categoría", alignRight: false },
  { id: "activo", label: "Estado", alignRight: false },
  { id: "" },
];

const CATEGORIA_COLORS = {
  CONTABLE: { bg: "#e3f2fd", color: "#1565c0" },
  TRIBUTARIO: { bg: "#fce4ec", color: "#880e4f" },
  REMUNERACIONES: { bg: "#e8f5e9", color: "#2e7d32" },
  SOCIETARIO: { bg: "#fff3e0", color: "#e65100" },
  OTRO: { bg: "#f3e5f5", color: "#6a1b9a" },
};

const FRECUENCIA_COLORS = {
  MES: { bg: "#e3f2fd", color: "#1565c0" },
  AÑO: { bg: "#ede7f6", color: "#4527a0" },
};

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #E0E0E0",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #F0F0F0",
    backgroundColor: "#FFFFFF",
  },
  statBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "8px 18px",
    borderRadius: 8,
    backgroundColor: "#FAFAFA",
    border: "1px solid #F0F0F0",
    minWidth: 72,
  },
  statNumber: {
    fontSize: "1.25rem",
    fontWeight: 700,
    lineHeight: 1.2,
    color: "#212121",
  },
  statLabel: {
    fontSize: "0.72rem",
    color: "#9E9E9E",
    fontWeight: 500,
    marginTop: 2,
  },
  tableContainer: {
    "& .MuiTableCell-head": {
      backgroundColor: "#FAFAFA",
      fontWeight: 600,
      fontSize: "0.813rem",
      color: "#424242",
      borderBottom: "2px solid #E0E0E0",
      whiteSpace: "nowrap",
      padding: "14px 16px",
    },
  },
  tableRow: {
    height: 56,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "#FAFAFA !important",
    },
  },
  tableCell: {
    fontSize: "0.875rem",
    color: "#212121",
    borderBottom: "1px solid #F5F5F5",
    padding: "12px 16px",
  },
  tableCellClickable: {
    cursor: "pointer",
  },
  nombreCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  frecuenciaBadge: {
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 10,
    whiteSpace: "nowrap",
    letterSpacing: "0.02em",
  },
  categoriaBadge: {
    fontSize: "0.75rem",
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 10,
    whiteSpace: "nowrap",
    display: "inline-block",
  },
  editButton: {
    color: "#BDBDBD",
    padding: 6,
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#1976D2",
      backgroundColor: "#E3F2FD",
    },
  },
  deleteButton: {
    color: "#BDBDBD",
    padding: 6,
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#EF5350",
      backgroundColor: "#FFEBEE",
    },
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: "center",
  },
}));

// ==================== HELPERS ====================

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (item) => item.nombre.toLowerCase().indexOf(query.toLowerCase()) !== -1,
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

// ==================== COMPONENTE PRINCIPAL ====================

export default function TipoPlanes() {
  const classes = useStyles();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("orden");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [elementoSeleccionado, setElementoSeleccionado] = useState(null);
  const [locale] = useState("esES");
  const { empleado: user } = useSelector((state) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();

  const { data: tipoPlanes = [] } = tiposApi.useGetTipoPlanesQuery();
  const { data: monedas = [] } = tiposApi.useGetTipoMonedaQuery();
  const [eliminarTipoPlanDB] = tiposApi.useDeleteTipoPlanesMutation();

  useHistorialVisita(user);

  const statsActivos = tipoPlanes.filter((p) => p.activo).length;
  const statsInactivos = tipoPlanes.length - statsActivos;

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(tipoPlanes.map((n) => n.nombre));
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredPlanes = applySortFilter(
    tipoPlanes,
    getComparator(order, orderBy),
    filterName,
  );

  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - filteredPlanes.length)
      : 0;

  const eliminarPlan = async (nombre) => {
    const result = await AlertaModalV2({
      title: "¿Eliminar este plan?",
      text: `Se eliminará "${nombre}". Esta acción es irreversible.`,
      confirmButtonText: "Sí, eliminar",
      confirmButtonColor: "#d32f2f",
      icon: "warning",
    }).fire();
    if (!result.isConfirmed) return;
    try {
      await eliminarTipoPlanDB(nombre).unwrap();
      enqueueSnackbar("Plan eliminado correctamente", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al eliminar el plan", { variant: "error" });
    }
  };

  return (
    <Page title="TAXTIC">
      <Container>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Button
            variant="contained"
            color="info"
            startIcon={<Icon icon={arrowFill} />}
            onClick={() => navigate("/dashboard/configuraciones")}
          >
            Volver
          </Button>
          <Typography variant="h4" gutterBottom>
            Tipo Planes
          </Typography>
          <Button
            variant="contained"
            startIcon={<Icon icon={plusFill} />}
            onClick={() => setOpen(true)}
          >
            Agregar Plan
          </Button>
        </Stack>

        <Card className={classes.card}>
          {/* Card Header: stats + buscador */}
          <Box className={classes.cardHeader}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              {/* Estadísticas */}
              <Stack direction="row" spacing={1.5}>
                <Box className={classes.statBox}>
                  <Typography className={classes.statNumber}>
                    {tipoPlanes.length}
                  </Typography>
                  <Typography className={classes.statLabel}>Total</Typography>
                </Box>
                <Box
                  className={classes.statBox}
                  style={{ borderColor: "#C8E6C9" }}
                >
                  <Typography
                    className={classes.statNumber}
                    style={{ color: "#2E7D32" }}
                  >
                    {statsActivos}
                  </Typography>
                  <Typography className={classes.statLabel}>Activos</Typography>
                </Box>
                <Box
                  className={classes.statBox}
                  style={{ borderColor: "#EEEEEE" }}
                >
                  <Typography
                    className={classes.statNumber}
                    style={{ color: "#9E9E9E" }}
                  >
                    {statsInactivos}
                  </Typography>
                  <Typography className={classes.statLabel}>
                    Inactivos
                  </Typography>
                </Box>
              </Stack>

              {/* Buscador */}
              <TextField
                size="small"
                placeholder="Buscar plan..."
                value={filterName}
                onChange={(e) => {
                  setFilterName(e.target.value);
                  setPage(0);
                }}
                style={{ width: 260 }}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#9E9E9E", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  style: { borderRadius: 8, backgroundColor: "#FAFAFA" },
                }}
              />
            </Stack>
          </Box>

          {/* Tabla */}
          <Scrollbar>
            <TableContainer
              className={classes.tableContainer}
              sx={{ minWidth: 800 }}
            >
              <Table>
                <TablaHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={tipoPlanes.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredPlanes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const {
                        nombre,
                        valor,
                        tipoMoneda,
                        categoria,
                        activo,
                        tipoFrecuencia_default,
                      } = row;
                      const isItemSelected = selected.indexOf(nombre) !== -1;
                      const categoriaColor = CATEGORIA_COLORS[categoria] ?? {
                        bg: "#F5F5F5",
                        color: "#616161",
                      };
                      const frecuenciaColor =
                        FRECUENCIA_COLORS[tipoFrecuencia_default] ?? null;

                      const handleAbrir = () => {
                        setElementoSeleccionado(row);
                        setOpenEditar(true);
                      };

                      return (
                        <TableRow
                          key={nombre}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          className={classes.tableRow}
                        >
                          {/* Nombre + badge frecuencia */}
                          <TableCell className={classes.tableCell}>
                            <Box className={classes.nombreCell}>
                              <Typography
                                variant="subtitle2"
                                style={{ fontWeight: 600 }}
                              >
                                {nombre}
                              </Typography>
                              {frecuenciaColor && (
                                <span
                                  className={classes.frecuenciaBadge}
                                  style={{
                                    backgroundColor: frecuenciaColor.bg,
                                    color: frecuenciaColor.color,
                                  }}
                                >
                                  {tipoFrecuencia_default}
                                </span>
                              )}
                            </Box>
                          </TableCell>

                          {/* Valor + Moneda combinados */}
                          <TableCell className={classes.tableCell}>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 500 }}
                            >
                              {valor != null ? valor : "—"}
                            </Typography>
                            {tipoMoneda && (
                              <Typography
                                variant="caption"
                                style={{ color: "#9E9E9E" }}
                              >
                                {tipoMoneda}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Categoría */}
                          <TableCell className={classes.tableCell}>
                            {categoria ? (
                              <span
                                className={classes.categoriaBadge}
                                style={{
                                  backgroundColor: categoriaColor.bg,
                                  color: categoriaColor.color,
                                }}
                              >
                                {categoria}
                              </span>
                            ) : (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Estado */}
                          <TableCell className={classes.tableCell}>
                            <Label
                              color={activo ? "success" : "default"}
                              variant="ghost"
                            >
                              {activo ? "Activo" : "Inactivo"}
                            </Label>
                          </TableCell>

                          {/* Acciones */}
                          <TableCell
                            className={classes.tableCell}
                            align="right"
                          >
                            <Tooltip title="Editar">
                              <IconButton
                                className={classes.editButton}
                                onClick={handleAbrir}
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                className={classes.deleteButton}
                                onClick={() => eliminarPlan(nombre)}
                                size="small"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 56 * emptyRows }}>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>

                {filteredPlanes.length === 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        align="center"
                        className={classes.emptyState}
                      >
                        <Typography variant="body2" color="textSecondary">
                          {filterName
                            ? `Sin resultados para "${filterName}"`
                            : "No hay planes registrados"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <ThemeProvider
            theme={(outerTheme) => createTheme(outerTheme, locales[locale])}
          >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredPlanes.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </ThemeProvider>
        </Card>
      </Container>

      {open && <AgregarPlan open={open} setOpen={setOpen} monedas={monedas} />}
      {openEditar && elementoSeleccionado && (
        <EditarPlan
          plan={elementoSeleccionado}
          openEditar={openEditar}
          setOpenEditar={setOpenEditar}
          monedas={monedas}
        />
      )}
    </Page>
  );
}
